import type React from "react";
import type { ReactNode } from "react";
import { Pressable, Text, View } from "react-native";
import { Check } from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Collapsible, CompactActionButton } from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";
import { useTaskSectionState } from "../hooks/useTaskSectionState";

type StepperProps = {
  children: ReactNode;
};

export function Stepper({ children }: StepperProps) {
  return (
    <View style={{ position: "relative", overflow: "visible" }}>
      <LinearGradient
        pointerEvents="none"
        colors={[
          "rgba(107, 85, 72, 0.18)",
          "rgba(107, 85, 72, 0.18)",
          "rgba(107, 85, 72, 0)",
        ]}
        locations={[0, 0.88, 1]}
        style={{
          position: "absolute",
          left: 11,
          top: 34,
          bottom: 10,
          width: 1,
        }}
      />
      {children}
    </View>
  );
}

type StepperSectionProps = {
  id: string;
  stepLabel: string;
  completionStepId?: string;
  showRail?: boolean;
  children: React.ReactNode;
};

export function StepperSection({
  id,
  stepLabel,
  completionStepId,
  showRail = true,
  children,
}: StepperSectionProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("StepperSection");
  const { completed, expanded, setCompleted, toggleExpanded } =
    useTaskSectionState(id, {
      defaultExpanded: true,
      completionStepId,
    });
  const handleCompletionPress = () => {
    void setCompleted(!completed);
  };
  const markIncompleteLabel = t(
    "homepage.review_concept.stepper_mark_incomplete_a11y",
    { params: { stepLabel } },
  );
  const markDoneLabel = t("homepage.review_concept.stepper_mark_done_a11y", {
    params: { stepLabel },
  });

  return (
    <View style={{ marginTop: 12, marginBottom: 10, position: "relative" }}>
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: 11,
          top: 22,
          bottom: 0,
          width: 1,
          backgroundColor: "rgba(107, 85, 72, 0.18)",
          opacity: showRail ? 1 : 0,
          zIndex: 0,
        }}
      />
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          zIndex: 1,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Pressable
            onPress={(event) => {
              event.stopPropagation();
              capturePress("stepper_completion_toggle");
              handleCompletionPress();
            }}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: completed }}
            accessibilityLabel={completed ? markIncompleteLabel : markDoneLabel}
            hitSlop={10}
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: completed
                ? semanticColors.status.success
                : semanticColors.surface.glass,
              borderWidth: completed ? 0 : 2,
              borderColor: "rgba(92, 52, 32, 0.18)",
              zIndex: 2,
            }}
          >
            {completed ? (
              <Check
                size={14}
                color={semanticColors.text.inverse}
                strokeWidth={3}
              />
            ) : (
              <Check
                size={13}
                color="rgba(107, 85, 72, 0.12)"
                strokeWidth={2}
              />
            )}
          </Pressable>
          <Pressable
            onPress={() => {
              capturePress("stepper_section_header");
              toggleExpanded();
            }}
            accessibilityRole="button"
            accessibilityLabel={
              expanded
                ? t("homepage.review_concept.stepper_collapse_a11y", {
                    params: { stepLabel },
                  })
                : t("homepage.review_concept.stepper_expand_a11y", {
                    params: { stepLabel },
                  })
            }
            accessibilityState={{ expanded }}
            hitSlop={8}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: EMBER.colors.textLight,
                letterSpacing: 1,
                textTransform: "uppercase",
                textDecorationLine: completed ? "line-through" : "none",
              }}
            >
              {stepLabel}
            </Text>
          </Pressable>
        </View>

        <CompactActionButton
          label={
            completed
              ? t("homepage.review_concept.stepper_done")
              : t("homepage.review_concept.stepper_mark_done")
          }
          action={
            completed
              ? "homepage.review_concept.stepper_done"
              : "homepage.review_concept.stepper_mark_done"
          }
          accessibilityLabel={completed ? markIncompleteLabel : markDoneLabel}
          variant={completed ? "selected" : "secondary"}
          size="sm"
          onPress={(event) => {
            event.stopPropagation();
            handleCompletionPress();
          }}
        />
      </View>

      <Collapsible expanded={expanded}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "stretch",
            gap: 12,
            marginTop: -8,
            paddingTop: 16,
            paddingBottom: 24,
            paddingRight: 8,
            marginRight: -8,
          }}
        >
          <View style={{ width: 22 }} />
          <View
            style={{
              flex: 1,
              gap: 10,
              paddingHorizontal: 8,
              marginHorizontal: -8,
            }}
          >
            {children}
          </View>
        </View>
      </Collapsible>
    </View>
  );
}
